import { getFormattedUTCDate } from "helpers";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PersonInfo, RadioButton, Select } from "ui";

import { useGetDeliveryCompanies } from "../../../../../deliveryCompanies/apis";
import { DriverSelector } from "../../../../../drivers";
import { useAssignDeliveryCompany, useAssignDriver } from "../../../../apis";
import { OrderPaymentMethods, OrderStatus } from "../../../../constants";
import { OrderInfoProps } from "../OrderModal.types";
import { OrderModalBlock } from "./blocks";

import styles from "../OrderModal.module.scss";

// Statuses where the BE still accepts a reassignment. Mirrors
// `order.utils.ts:checkThatStatusValidToAssign{Driver,DeliveryCompany}` —
// keep them in sync. Company reassignment is the tighter set: once a driver
// has the package on the way, BE refuses to change the company.
const DRIVER_REASSIGN_STATUSES: OrderStatus[] = [
  OrderStatus.Placed,
  OrderStatus.Collecting,
  OrderStatus.AssignedToCompany,
  OrderStatus.PickedUpByCompany,
  OrderStatus.AssignedToDriver,
  OrderStatus.Active,
  OrderStatus.InDelivery
];

const COMPANY_REASSIGN_STATUSES: OrderStatus[] = [
  OrderStatus.Placed,
  OrderStatus.Collecting,
  OrderStatus.Active,
  OrderStatus.AssignedToCompany,
  OrderStatus.PickedUpByCompany,
  OrderStatus.AssignedToDriver,
  OrderStatus.ReturnPlaced,
  OrderStatus.ReturnApproved,
  OrderStatus.ReturnReadyForDeliveryToCustomer
];

export const OrderInfo = ({
  orderDetails,
  refetchOrder,
  refetchOrders
}: OrderInfoProps) => {
  const { t } = useTranslation();
  const orderPayment =
    orderDetails?.paymentMethod &&
    OrderPaymentMethods[orderDetails.paymentMethod];

  const status = orderDetails?.status as OrderStatus | undefined;
  const canReassignDriver =
    !!status && DRIVER_REASSIGN_STATUSES.includes(status);
  const canReassignCompany =
    !!status && COMPANY_REASSIGN_STATUSES.includes(status);

  // Default to whichever mode matches the current assignment. With a company
  // already set the admin most likely wants to swap something inside that
  // company (change company / change driver of the company), so we land on
  // the Company tab which shows both selectors stacked. Without a company,
  // start on Driver — that's the individual-driver path.
  const [assigneeMode, setAssigneeMode] = useState<"driver" | "company">(() =>
    orderDetails?.deliveryCompanyId ? "company" : "driver"
  );

  // If status leaves the company-reassign window while admin is on the
  // Company tab and the order is fully individual (no company), nothing on
  // the Company tab is meaningful anymore — slide back to Driver.
  useEffect(() => {
    if (
      assigneeMode === "company" &&
      !canReassignCompany &&
      !orderDetails?.deliveryCompanyId &&
      canReassignDriver
    ) {
      setAssigneeMode("driver");
    }
  }, [
    assigneeMode,
    canReassignCompany,
    canReassignDriver,
    orderDetails?.deliveryCompanyId
  ]);

  const { isLoading: isLoadingAssignDriver, mutate: mutateAssignDriver } =
    useAssignDriver({
      onSuccess: () => {
        refetchOrder();
        refetchOrders();
      }
    });

  const { isLoading: isLoadingAssignCompany, mutate: mutateAssignCompany } =
    useAssignDeliveryCompany({
      onSuccess: () => {
        refetchOrder();
        refetchOrders();
      }
    });

  const { companies, isLoading: isLoadingCompanies } = useGetDeliveryCompanies(
    {
      skip: 0,
      take: 50,
      isDeleted: false
    },
    { enabled: canReassignCompany }
  );

  const companyOptions =
    companies?.map((company) => ({
      label: company.name,
      value: company.id
    })) ?? [];

  const onSelectDriver = (driverId: string | string[]) => {
    const id = Array.isArray(driverId) ? driverId[0] : driverId;
    if (!id || id === orderDetails?.driver?.id) return;
    mutateAssignDriver({ orderId: orderDetails.id, driverId: id });
  };

  const onSelectCompany = (deliveryCompanyId: string | string[]) => {
    const id = Array.isArray(deliveryCompanyId)
      ? deliveryCompanyId[0]
      : deliveryCompanyId;
    if (!id || id === orderDetails?.deliveryCompanyId) return;
    mutateAssignCompany({ orderId: orderDetails.id, deliveryCompanyId: id });
  };

  return (
    <div className={styles.orderModal__section}>
      <div className={styles.orderModal__info}>
        <OrderModalBlock label={t("Order Number")}>
          <p>{orderDetails?.id}</p>
        </OrderModalBlock>
        <OrderModalBlock label={t("Delivery Address")}>
          <div className={styles.orderModal__info__delivery}>
            <div className={styles.orderModal__info__deliveryName}>
              {orderDetails?.deliveryAddress.fullName}
            </div>
            <p>{orderDetails?.deliveryAddress.phoneNumber}</p>
            <p>
              {orderDetails?.deliveryAddress.address},{" "}
              {orderDetails?.deliveryAddress.deliveryZone.neighborhood},{" "}
              {orderDetails?.deliveryAddress.deliveryZone.city},{" "}
              {orderDetails?.deliveryAddress.deliveryZone.country}
            </p>
          </div>
        </OrderModalBlock>
        <OrderModalBlock label={t("Payment Method")}>
          <div className={styles.orderModal__info__payment}>
            {orderPayment?.icon}
            <p>{orderPayment?.label}</p>
          </div>
        </OrderModalBlock>
        <OrderModalBlock label={t("Order create date")}>
          <p>
            {moment(orderDetails?.createdAt).format("DD MMMM, YYYY")} {t("at")}{" "}
            {moment(orderDetails?.createdAt).format("HH:mm")}
          </p>
          <OrderModalBlock label={t("Delivery date")}>
            <p>
              {getFormattedUTCDate(orderDetails?.deliveryDate, "DD MMMM, YYYY")}
            </p>
          </OrderModalBlock>
        </OrderModalBlock>
      </div>
      <OrderModalBlock label={t("Specific Instructions")}>
        {orderDetails?.deliveryAddress.instructions ? (
          <p>{orderDetails?.deliveryAddress.instructions}</p>
        ) : (
          <p className={styles.orderModal__noInfoText}>{t("No instruction")}</p>
        )}
      </OrderModalBlock>
      <div className={styles.orderModal__driverBlock}>
        <div className={styles.orderModal__driverBlock__title}>
          {t("Assignee")}
        </div>

        {canReassignDriver ? (
          <>
            {/* Toggle is always available so the admin can flip to individual
              driver mode regardless of the current assignment. */}
            <div className={styles.orderModal__assigneeToggle}>
              <RadioButton
                label={t("Driver")}
                name="assigneeMode"
                checked={assigneeMode === "driver"}
                onChange={() => setAssigneeMode("driver")}
              />
              <RadioButton
                label={t("Delivery company")}
                name="assigneeMode"
                checked={assigneeMode === "company"}
                onChange={() => setAssigneeMode("company")}
              />
            </div>

            {assigneeMode === "company" ? (
              <>
                {canReassignCompany ? (
                  <Select
                    placeholder={t("Choose Delivery Company")}
                    value={orderDetails?.deliveryCompanyId ?? undefined}
                    onChange={onSelectCompany}
                    options={companyOptions}
                    isOptionsLoading={
                      isLoadingAssignCompany || isLoadingCompanies
                    }
                  />
                ) : orderDetails?.deliveryCompany ? (
                  /* Company is past the reassign window (e.g. InDelivery) —
                    show it read-only so the admin still sees which one holds
                    the package, while keeping the driver selector live. */
                  <PersonInfo
                    personName={orderDetails.deliveryCompany.name}
                    image={
                      orderDetails.deliveryCompany.avatar ?? undefined
                    }
                    wrapperClassName={
                      styles.orderModal__driverBlock__personInfo
                    }
                    nameClassName={
                      styles.orderModal__driverBlock__personName
                    }
                    dimensions={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%"
                    }}
                  />
                ) : null}

                {orderDetails?.deliveryCompanyId ? (
                  <DriverSelector
                    value={orderDetails?.driver?.id}
                    onChange={onSelectDriver}
                    isLoading={isLoadingAssignDriver}
                    deliveryCompanyId={orderDetails.deliveryCompanyId}
                  />
                ) : null}
              </>
            ) : (
              /* Driver tab — individual / cross-company pool. If a company
                is already on the order the current driver belongs to that
                company (not an individual), so don't preselect — that would
                hide the placeholder and make it look as if an individual
                driver were chosen. */
              <DriverSelector
                value={
                  orderDetails?.deliveryCompanyId
                    ? undefined
                    : orderDetails?.driver?.id
                }
                onChange={onSelectDriver}
                isLoading={isLoadingAssignDriver}
              />
            )}
          </>
        ) : (
          <>
            {orderDetails?.deliveryCompany ? (
              <PersonInfo
                personName={orderDetails.deliveryCompany.name}
                image={orderDetails.deliveryCompany.avatar ?? undefined}
                wrapperClassName={styles.orderModal__driverBlock__personInfo}
                nameClassName={styles.orderModal__driverBlock__personName}
                dimensions={{ width: 30, height: 30, borderRadius: "50%" }}
              />
            ) : null}
            {orderDetails?.deliveryCompany && orderDetails?.driver ? (
              <span
                className={styles.orderModal__driverBlock__assignedToLabel}
              >
                {t("Assigned to")}
              </span>
            ) : null}
            {orderDetails?.driver ? (
              <PersonInfo
                personName={`${orderDetails?.driver.firstname} ${orderDetails?.driver.lastname}`}
                image={orderDetails?.driver.image}
                wrapperClassName={styles.orderModal__driverBlock__personInfo}
                nameClassName={styles.orderModal__driverBlock__personName}
                dimensions={{ width: 30, height: 30, borderRadius: "50%" }}
              />
            ) : !orderDetails?.deliveryCompany ? (
              <p className={styles.orderModal__noInfoText}>
                {t("No driver has been assigned")}
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
